import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import config from 'constants/config'
import ButtonGroupToggle from 'components/Common/ButtonGroupToggle'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { connect } from 'react-redux'
import type { ButtonToggleItem } from 'components/Common/ButtonGroupToggle/ButtonGroupToggleItem'

interface Props {
  onSelectSpecific: () => void
  onSelectAnytime: () => void
  onSelectFlexible?: () => void
  disableFlexible?: boolean // this needs to be negative logic to avoid impacting existing code
  anytimeDateSelected: boolean
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  className?: string
  isSpecificSearchEnabled?: boolean
}

interface MappedStateProps {
  isFlexibleSearchEnabled: boolean
}

/**
 * UI component to allow user to select/click Specific dates, Flexible dates or Anytime in button format
 */
function SearchDateToggle({
  className,
  onSelectSpecific,
  onSelectFlexible,
  onSelectAnytime,
  anytimeDateSelected,
  dateSearchOptionId,
  isFlexibleSearchEnabled,
  isSpecificSearchEnabled = true,
}: Props & MappedStateProps) {
  const intl = useIntl()

  const flexibleDatesLabel = intl.formatMessage({
    id: 'd0qShlzcfC',
    defaultMessage: 'Flexible dates',
    description: 'Toggle label for flexible dates option in search date picker',
  })
  const specificDatesLabel = intl.formatMessage({
    id: 'oXtaeJy1Md',
    defaultMessage: 'Specific dates',
    description: 'Toggle label for specific dates option when flexible search is enabled',
  })
  const iveGotDatesLabel = intl.formatMessage({
    id: '75YSHpPoZp',
    defaultMessage: 'I’ve got dates',
    description: 'Toggle label for specific dates option when flexible search is disabled',
  })
  const anytimeLabel = intl.formatMessage({
    id: 'eVY8ROSdIh',
    defaultMessage: 'Anytime',
    description: 'Toggle label for anytime dates option when flexible search is enabled',
  })
  const imFlexibleLabel = intl.formatMessage({
    id: 'p4WX08pMff',
    defaultMessage: 'I’m flexible',
    description: 'Toggle label for flexible dates option when flexible search is disabled',
  })

  const flexibleDatesToggle = useMemo(() => ({
    id: DATE_SEARCH_OPTION_IDS.FLEXIBLE,
    label: flexibleDatesLabel,
  }), [flexibleDatesLabel])

  const specificDatesToggle = useMemo(() => ({
    id: DATE_SEARCH_OPTION_IDS.SPECIFIC,
    label: isFlexibleSearchEnabled ? specificDatesLabel : iveGotDatesLabel,
  }), [isFlexibleSearchEnabled, specificDatesLabel, iveGotDatesLabel])

  const anytimeDatesToggle = useMemo(() => ({
    id: DATE_SEARCH_OPTION_IDS.ANYTIME,
    label: isFlexibleSearchEnabled ? anytimeLabel : imFlexibleLabel,
  }), [isFlexibleSearchEnabled, anytimeLabel, imFlexibleLabel])

  const items = useMemo(
    () => {
      const items: Array<{ id: string, label: string }> = []
      if (isSpecificSearchEnabled) items.push(specificDatesToggle)
      if (isFlexibleSearchEnabled) items.push(flexibleDatesToggle)
      items.push(anytimeDatesToggle)
      return items
    },
    [isSpecificSearchEnabled, isFlexibleSearchEnabled, specificDatesToggle, flexibleDatesToggle, anytimeDatesToggle],
  )
  const activeItemByOptionId = useMemo(
    () =>
      items.find((toggle) => {
        return toggle.id === dateSearchOptionId
      }),
    [items, dateSearchOptionId],
  )

  const activeItemByAnytimeDateSelected = anytimeDateSelected ?
    anytimeDatesToggle :
    specificDatesToggle
  const activeItem =
    isFlexibleSearchEnabled && dateSearchOptionId ?
      activeItemByOptionId :
      activeItemByAnytimeDateSelected

  const onItemClick = useCallback(
    (item: ButtonToggleItem, e?: React.MouseEvent) => {
      // prevent the search form submit
      e?.preventDefault()
      if (item === specificDatesToggle) {
        onSelectSpecific()
      } else if (item === flexibleDatesToggle) {
        onSelectFlexible?.()
      } else if (item === anytimeDatesToggle) {
        onSelectAnytime()
      }
    },
    [onSelectFlexible, onSelectSpecific, onSelectAnytime, specificDatesToggle, flexibleDatesToggle, anytimeDatesToggle],
  )

  return (
    <ButtonGroupToggle
      className={className}
      items={items}
      activeItem={activeItem}
      onItemClick={onItemClick}
      data-testid="search-toggle"
    />
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((_state, ownProps) => ({
  isFlexibleSearchEnabled: !ownProps.disableFlexible && config.ENABLE_FLEXIBLE_SEARCH,
}))(SearchDateToggle)
